/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useEffect, useState } from 'react';
import AnswerState from '../constants/types';
import ChallengeView from '../components/challengeView';
import { ErrorCode } from '../interface';
import { ProofOfWorkActionType } from '../store/action';
import sleep from '../../../../common/sleep';
import useProofOfWorkContext from '../hooks/useProofOfWorkContext';
import { mapProofOfWorkErrorToChallengeErrorCode } from '../constants/resources';

const ProofOfWork: React.FC = () => {
  const {
    state: { sessionId, requestService },
    dispatch
  } = useProofOfWorkContext();

  /*
   * Component State
   */

  const [puzzleArtifact, setPuzzleArtifact] = useState<string | null>(null);
  const [puzzleSolution, setPuzzleSolution] = useState<string | null>(null);
  const [answerState, setAnswerState] = useState<AnswerState>(AnswerState.INITIAL);

  /*
   * Event Handlers
   */

  const clearChallenge = () => {
    setPuzzleArtifact(null);
    setPuzzleSolution(null);
    setAnswerState(AnswerState.INITIAL);
  };

  // TODO: place holder for actual puzzle solver
  const solvePuzzle = async () => {
    if (puzzleArtifact == null || answerState !== AnswerState.READY_TO_COMPUTE) {
      return;
    }
    setAnswerState(AnswerState.COMPUTING);
    await sleep(5000);
    setPuzzleSolution('2000');
    setAnswerState(AnswerState.COMPUTING_DONE);
  };

  const checkAnswer = async () => {
    if (puzzleSolution == null) {
      return;
    }
    const result = await requestService.proofOfWork.verifyPuzzle(sessionId, puzzleSolution);

    // Request failure is either an unknown error (show error) or session is invalidated
    if (result.isError) {
      dispatch({
        type: ProofOfWorkActionType.SET_CHALLENGE_INVALIDATED,
        onChallengeInvalidatedData: {
          errorCode: mapProofOfWorkErrorToChallengeErrorCode(result.error)
        }
      });
      return;
    }

    // CASE: Correct answer.
    if (result.value.answerCorrect) {
      // CASE: Answer correct; complete challenge.
      setAnswerState(AnswerState.VERIFIED_CORRECT);
      dispatch({
        type: ProofOfWorkActionType.SET_CHALLENGE_COMPLETED,
        onChallengeCompletedData: {
          redemptionToken: result.value.redemptionToken
        }
      });
    } else {
      // CASE: Incorrect answer
      setAnswerState(AnswerState.VERIFIED_INCORRECT);
      dispatch({
        type: ProofOfWorkActionType.SET_CHALLENGE_INVALIDATED,
        onChallengeInvalidatedData: {
          errorCode: ErrorCode.ANSWER_INVALID
        }
      });
    }
  };

  const loadChallenge = async () => {
    clearChallenge();
    const resultGetPuzzle = await requestService.proofOfWork.getPuzzle(sessionId);
    if (resultGetPuzzle.isError) {
      dispatch({
        type: ProofOfWorkActionType.SET_CHALLENGE_INVALIDATED,
        onChallengeInvalidatedData: {
          errorCode: ErrorCode.UNKNOWN
        }
      });
      return;
    }
    setPuzzleArtifact(resultGetPuzzle.value.artifact);
    setAnswerState(AnswerState.READY_TO_COMPUTE);
  };

  const handleAnswerStateChange = async () => {
    switch (answerState) {
      case AnswerState.READY_TO_COMPUTE: {
        await solvePuzzle();
        break;
      }
      case AnswerState.COMPUTING_DONE: {
        await checkAnswer();
        break;
      }
      default: {
        break;
      }
    }
  };

  // Challenge loading effect:
  useEffect(() => {
    // eslint-disable-next-line no-void
    void loadChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-void
    void handleAnswerStateChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerState]);

  /*
   * Component Markup
   */
  return <ChallengeView answerState={answerState} />;
};

export default ProofOfWork;
